import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { getResolutionNotes, escalateChat, getErrorMessage, type ResolutionNote } from '../../utils/api'
import { toast } from '../shared/NotificationToast'
import { useAuth } from '../../contexts/AuthContext'

const RESOLVED_TAGS = new Set(['resolved', 'closed', 'solved'])
function resolutionStatus(n: ResolutionNote): 'Resolved' | 'Unresolved' {
  const t = (n.resolution_tag || '').toLowerCase()
  return t && RESOLVED_TAGS.has(t) ? 'Resolved' : 'Unresolved'
}
function fmtDate(s?: string | null): string {
  if (!s) return '—'
  const d = new Date(s)
  return isNaN(d.getTime()) ? '—' : d.toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

type SortKey = 'recent' | 'status' | 'type' | 'escalated'

export const ResolutionNotesView: React.FC<{ mineOnly?: boolean }> = ({ mineOnly = false }) => {
  const navigate = useNavigate()
  const { currentAgent } = useAuth()
  const [notes, setNotes] = useState<ResolutionNote[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [sort, setSort] = useState<SortKey>('recent')
  const [escalatedOnly, setEscalatedOnly] = useState(false)
  const [busyId, setBusyId] = useState<string | number | null>(null)

  const load = async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await getResolutionNotes()
      setNotes(res.notes || [])
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load resolution notes'))
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => { load() }, [])

  const visible = useMemo(() => {
    let list = escalatedOnly ? notes.filter((n) => n.escalated) : [...notes]
    if (mineOnly && currentAgent) {
      list = list.filter((n) => String(n.agent_id ?? '') === String(currentAgent.id))
    }
    list.sort((a, b) => {
      if (sort === 'status') return resolutionStatus(a).localeCompare(resolutionStatus(b))
      if (sort === 'type') return (a.resolution_tag || '~').localeCompare(b.resolution_tag || '~')
      if (sort === 'escalated') return Number(b.escalated) - Number(a.escalated)
      return new Date(b.closed_at || b.created_at || 0).getTime() - new Date(a.closed_at || a.created_at || 0).getTime()
    })
    return list
  }, [notes, sort, escalatedOnly, mineOnly, currentAgent])

  const toggleEscalate = async (n: ResolutionNote) => {
    setBusyId(n.chat_id)
    try {
      await escalateChat(n.chat_id, !n.escalated)
      setNotes((prev) => prev.map((x) => (x.chat_id === n.chat_id ? { ...x, escalated: !n.escalated, escalated_at: !n.escalated ? new Date().toISOString() : null } : x)))
      toast.success(!n.escalated ? 'Issue escalated' : 'Escalation cleared')
    } catch (err) {
      toast.error('Failed', getErrorMessage(err, 'Could not update escalation'))
    } finally {
      setBusyId(null)
    }
  }

  const selectCls = 'px-3 py-1.5 rounded-lg bg-surface border border-border text-sm text-txt-primary outline-none focus:border-brand-500'
  const escalatedCount = notes.filter((n) => n.escalated).length

  return (
    <div className="h-full flex flex-col">
      {/* Title + tabs live in the Chats shell above; keep the sort/escalate controls. */}
      <div className="px-6 py-3 bg-surface border-b border-border flex items-center gap-3 flex-wrap">
        <label className="text-sm text-txt-secondary flex items-center gap-2">
          Sort by
          <select value={sort} onChange={(e) => setSort(e.target.value as SortKey)} className={selectCls}>
            <option value="recent">Most recent</option>
            <option value="status">Resolution status</option>
            <option value="type">Resolution type</option>
            <option value="escalated">Escalated first</option>
          </select>
        </label>
        <button
          onClick={() => setEscalatedOnly((v) => !v)}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
            escalatedOnly ? 'bg-red-50 text-red-600 border-red-200 dark:bg-red-900/20 dark:text-red-300' : 'border-border text-txt-secondary hover:bg-surface-hover'
          }`}
        >
          Escalated only{escalatedCount ? ` (${escalatedCount})` : ''}
        </button>
        <span className="ml-auto text-xs text-txt-tertiary">{visible.length} note{visible.length === 1 ? '' : 's'}</span>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        {error && <div className="mb-4 px-4 py-3 rounded-lg bg-error-light text-error text-sm">{error}</div>}
        {loading ? (
          <p className="text-sm text-txt-secondary text-center py-12">Loading resolution notes…</p>
        ) : visible.length === 0 ? (
          <p className="text-sm text-txt-secondary text-center py-12">{escalatedOnly ? 'No escalated issues.' : 'No resolution notes yet.'}</p>
        ) : (
          <div className="flex flex-col gap-3 max-w-3xl">
            {visible.map((n) => {
              const status = resolutionStatus(n)
              const who = n.visitor_name || n.visitor_email || 'Unknown'
              const company = n.company_name || n.account_alias || null
              const avatarSeed = company || who
              return (
                <div key={String(n.chat_id)} className={`rounded-xl bg-surface border p-4 ${n.escalated ? 'border-red-300 dark:border-red-900/50' : 'border-border'}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 min-w-0">
                      {/* Company avatar — logo if we have it, otherwise an initial (like normal chats) */}
                      {n.company_logo_url ? (
                        <img src={n.company_logo_url} alt="" className="w-9 h-9 rounded-full object-cover bg-surface-secondary border border-border shrink-0" />
                      ) : (
                        <span className="w-9 h-9 rounded-full bg-brand-100 dark:bg-brand-900/40 text-brand-700 dark:text-brand-300 text-xs font-bold flex items-center justify-center shrink-0">
                          {(avatarSeed || '?').slice(0, 2).toUpperCase()}
                        </span>
                      )}
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-semibold text-txt-primary truncate">{who}</span>
                          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${status === 'Resolved' ? 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300' : 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'}`}>{status}</span>
                          {n.resolution_tag && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-surface-tertiary text-txt-secondary capitalize">{n.resolution_tag.replace(/_/g, ' ')}</span>}
                          {n.escalated && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-300">⚑ Escalated</span>}
                        </div>
                        {company && <p className="text-xs text-txt-secondary mt-0.5 truncate">{company}</p>}
                        {n.visitor_email && <p className="text-xs text-txt-tertiary truncate">{n.visitor_email}</p>}
                      </div>
                    </div>
                    <span className="text-[11px] text-txt-tertiary whitespace-nowrap shrink-0">{fmtDate(n.closed_at || n.created_at)}</span>
                  </div>

                  <p className="text-sm text-txt-secondary mt-2 whitespace-pre-line">{n.resolution_summary}</p>

                  <div className="flex items-center gap-2 mt-3">
                    <button onClick={() => navigate(`/queue/${n.chat_id}`)} className="px-3 py-1.5 rounded-lg text-xs font-medium border border-border text-txt-secondary hover:bg-surface-hover transition-colors">
                      Open chat
                    </button>
                    <button
                      onClick={() => toggleEscalate(n)}
                      disabled={busyId === n.chat_id}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors disabled:opacity-50 ${
                        n.escalated ? 'border border-border text-txt-secondary hover:bg-surface-hover' : 'bg-red-600 text-white hover:bg-red-700'
                      }`}
                    >
                      {n.escalated ? 'Clear escalation' : '⚑ Escalate'}
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
